import { useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';

/**
 * Karte der Verwaltung mit auf- und zuklappbarem Kopf. Der Zustand je Karte bleibt im Browser
 * erhalten (localStorage), damit lange Seiten so aussehen, wie man sie verlassen hat.
 */

function storageKey(id: string): string {
  return `evercast.card.${id}`;
}

export function useCollapsed(id: string, defaultOpen: boolean): [boolean, () => void] {
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const stored = window.localStorage.getItem(storageKey(id));
      return stored === null ? defaultOpen : stored === 'open';
    } catch {
      return defaultOpen;
    }
  });
  const toggle = () => {
    setOpen((o) => {
      try {
        window.localStorage.setItem(storageKey(id), o ? 'closed' : 'open');
      } catch {
        /* privater Modus o. ä. – dann eben nur für diese Seite */
      }
      return !o;
    });
  };
  return [open, toggle];
}

interface CardShellProps {
  /** Schlüssel fürs Merken des Zustands, z. B. "webinar.video". */
  id: string;
  title: ReactNode;
  /** Rechts im Kopf: Schalter, Kennzeichen, Links – bleibt auch zugeklappt sichtbar. */
  right?: ReactNode;
  defaultOpen?: boolean;
  /** Von außen gesteuert (z. B. wenn das Öffnen etwas nachlädt). */
  open?: boolean;
  onToggle?: () => void;
  as?: 'div' | 'form';
  onSubmit?: (e: FormEvent) => void;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

export function CardShell({ id, title, right, defaultOpen = true, open: controlledOpen, onToggle, as = 'div', onSubmit, className, style, children }: CardShellProps) {
  const [ownOpen, ownToggle] = useCollapsed(id, defaultOpen);
  const open = controlledOpen ?? ownOpen;
  const toggle = onToggle ?? ownToggle;
  const Tag = as;
  return (
    <Tag className={`card${open ? '' : ' card--closed'}${className ? ` ${className}` : ''}`} {...(style ? { style } : {})} {...(as === 'form' && onSubmit ? { onSubmit } : {})}>
      <div className="card__head">
        <button type="button" className="card__toggle" aria-expanded={open} onClick={toggle}>
          <span className="card__chevron" aria-hidden="true" />
          <h2 className="card__title">{title}</h2>
        </button>
        {right}
      </div>
      {open && children}
    </Tag>
  );
}
